import { Reorder, useDragControls } from "framer-motion";
import { toast } from "sonner";
import { GripVertical, ChevronUp, ChevronDown, RotateCcw, Eye, EyeOff } from "lucide-react";
import * as Icons from "lucide-react";

const Row = ({ c, i, n, label, move, hidden, toggle }) => {
  const ctrl = useDragControls();
  const Icon = Icons[c.icon] || Icons.PawPrint;
  return (
    <Reorder.Item value={c.id} dragListener={false} dragControls={ctrl} data-testid={`cat-order-row-${c.id}`}
      className={`flex items-center gap-2 rounded-xl border px-2 py-1.5 select-none ${hidden ? "border-dashed border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`}>
      <button type="button" onPointerDown={(e) => ctrl.start(e)} className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-[#12234A] touch-none p-1" aria-label="Drag to reorder" data-testid={`cat-order-drag-${c.id}`}>
        <GripVertical size={16} />
      </button>
      <span className="w-6 text-xs font-bold text-slate-400">{i + 1}</span>
      <Icon size={15} className="text-[#F5551E]" />
      <span className={`flex-1 text-sm font-medium ${hidden ? "text-slate-400 line-through" : "text-[#12234A]"}`}>{label}</span>
      {hidden && <span className="text-[11px] font-semibold text-slate-500 bg-slate-200/70 rounded-full px-2 py-0.5" data-testid={`cat-order-hidden-badge-${c.id}`}>Hidden from homepage</span>}
      <button type="button" onClick={toggle} className={`p-1 ${hidden ? "text-slate-400 hover:text-[#12234A]" : "text-[#F5551E] hover:text-[#12234A]"}`}
        aria-label={hidden ? "Show on homepage" : "Hide from homepage"} title={hidden ? "Show on homepage" : "Hide from homepage"} data-testid={`cat-order-hide-${c.id}`}>
        {hidden ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
      <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-slate-400 hover:text-[#12234A] disabled:opacity-30" aria-label="Move up" data-testid={`cat-order-up-${c.id}`}><ChevronUp size={16} /></button>
      <button type="button" onClick={() => move(i, 1)} disabled={i === n - 1} className="p-1 text-slate-400 hover:text-[#12234A] disabled:opacity-30" aria-label="Move down" data-testid={`cat-order-down-${c.id}`}><ChevronDown size={16} /></button>
    </Reorder.Item>
  );
};

export const homeTiles = (items, order, hidden = []) => sortByOrder(items.filter((c) => !hidden.includes(c.id)), order);

export const sortByOrder = (items, order) => {
  if (!order?.length) return items;
  const pos = (id) => { const i = order.indexOf(id); return i < 0 ? order.length : i; };
  return [...items].sort((a, b) => pos(a.id) - pos(b.id));
};

export function CategoryOrder({ tiles, order, setOrder, labels, hidden = [], setHidden }) {
  const ids = sortByOrder(tiles, order).map((c) => c.id);
  const byId = Object.fromEntries(tiles.map((c) => [c.id, c]));
  const visible = ids.filter((id) => !hidden.includes(id)).length;
  const toggle = (id) => {
    if (hidden.includes(id)) { setHidden(hidden.filter((x) => x !== id)); return; }
    if (visible <= 1) { toast.error("Keep at least one category on the homepage"); return; }
    setHidden([...hidden, id]);
  };
  const move = (i, d) => { const next = [...ids]; [next[i], next[i + d]] = [next[i + d], next[i]]; setOrder(next); };
  return (
    <div className="rounded-xl border border-slate-200 p-3 mb-4" data-testid="cat-order">
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-sm font-semibold text-[#12234A]">Order in the homepage row <span className="font-normal text-slate-500" data-testid="cat-order-visible-count">({visible} of {ids.length} showing)</span></p>
        <button type="button" onClick={() => setOrder([])} disabled={!order?.length} className="text-xs font-semibold text-slate-500 hover:text-[#F5551E] flex items-center gap-1 disabled:opacity-40" data-testid="cat-order-reset">
          <RotateCcw size={12} /> Default order
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-3">Drag the handle (or use the arrows) — the first 6 show first on computers. Tap the eye to hide a tile from the homepage row; the category stays everywhere else on the site. The top menu keeps its own order. Press “Save tiles” below to publish.</p>
      <Reorder.Group axis="y" values={ids} onReorder={setOrder} className="space-y-1.5">
        {ids.map((id, i) => <Row key={id} c={byId[id]} i={i} n={ids.length} label={labels[id] || byId[id].label} move={move} hidden={hidden.includes(id)} toggle={() => toggle(id)} />)}
      </Reorder.Group>
    </div>
  );
}
